import { logger } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';

import type { UiOpenArtifactWire } from '../../../contracts/ui-open-artifact.types';
import {
  requestSessionSidePanel,
  sidePanelFromOpenArtifact,
} from './openSessionSidePanel';
import type { VoiceUiActionsPort } from './voice-ui-actions.port';

/**
 * Classic Scribe session href — no unused query hints. Tab focus is applied
 * via ``sessionWindowsAtomFamily`` (see ``requestSessionTabFocus``).
 */
const scribeHrefForOpenArtifact = (data: UiOpenArtifactWire): string | null => {
  const sessionId = data.session_id?.trim();
  if (!sessionId) return null;
  if (data.type === 'session' || data.type === 'note' || data.type === 'document') {
    return `/scribe/sessions/${encodeURIComponent(sessionId)}`;
  }
  return null;
};

/**
 * Apply ``ui.open_artifact`` on classic Scribe (not Heidi Work).
 * Navigates / focuses tabs / opens side panels — never the Work canvas.
 */
export const applyClassicScribeOpenArtifact = (
  data: UiOpenArtifactWire,
  port: VoiceUiActionsPort
): boolean => {
  const sessionId = data.session_id?.trim() || getCurrentSessionId()?.trim() || '';
  const tabName = port.resolveSessionTabName(data);

  // Standalone patient profile page — not the Work canvas, and not the
  // current session's Patient Summary tab (that stayed on Max while
  // Heidi claimed Sergio was open).
  if (data.type === 'patient') {
    const patientId = data.patient_id?.trim();
    if (!patientId) {
      logger.warn('voice: ui.open_artifact patient on Scribe missing patient_id');
      return false;
    }
    // Encode so agent-supplied ids cannot inject path segments or query/hash.
    const href = `/patients/${encodeURIComponent(patientId)}`;
    // Don't log patient ids — logger → Datadog; ids are indirect PHI.
    logger.info('voice: ui.open_artifact patient → /patients on classic Scribe');
    void port.onNavigate(href);
    return true;
  }

  // Tasks / clinical codes are side panels on classic Scribe, not session
  // tabs. Navigating to the session alone looked like success while the
  // panel stayed closed (especially in-session, where push is a no-op).
  const sidePanel = sidePanelFromOpenArtifact(data.type);
  if (sidePanel) {
    if (!sessionId) {
      logger.warn('voice: ui.open_artifact tasks/codes on Scribe missing session_id');
      return false;
    }
    requestSessionSidePanel(sessionId, sidePanel, port);
    const currentSessionId = getCurrentSessionId();
    if (sessionId !== currentSessionId) {
      const href = `/scribe/sessions/${encodeURIComponent(sessionId)}`;
      logger.info('voice: ui.open_artifact → navigate + side panel on classic Scribe', {
        type: data.type,
      });
      void port.onNavigate(href);
    } else {
      logger.info('voice: ui.open_artifact → side panel on classic Scribe', {
        type: data.type,
      });
    }
    return true;
  }

  if (sessionId && tabName) {
    port.focusSessionTab(sessionId, data);
  }
  const currentSessionId = getCurrentSessionId();
  const href = scribeHrefForOpenArtifact(data);
  if (href && sessionId && sessionId !== currentSessionId) {
    logger.info('voice: ui.open_artifact → navigate + focus on classic Scribe', {
      type: data.type,
    });
    void port.onNavigate(href);
    return true;
  }
  if (sessionId && tabName) {
    logger.info('voice: ui.open_artifact → focus tab on classic Scribe', {
      type: data.type,
      tab: tabName,
    });
    return true;
  }
  if (href) {
    void port.onNavigate(href);
    return true;
  }
  // type=session with no tab while already on that session: nothing to do,
  // but still a successful no-op open.
  if (data.type === 'session' && sessionId && sessionId === currentSessionId) {
    return true;
  }
  logger.warn('voice: ui.open_artifact ignored on classic Scribe', {
    type: data.type,
  });
  return false;
};
